import { useMemo } from "react";

import { ParentSize } from "@visx/responsive";
import { reduce as reduceP } from "lodash";
import { groupBy, reduce } from "lodash/fp";

import { Box, Skeleton, Stack, Typography } from "@mui/material";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { ParsedMetric } from "@app/analytics/meetops/metrics";
import { DateRange, MeetOpsQuery } from "@app/analytics/meetops/types";
import { TrendSeriesItem } from "@app/analytics/types";
import ErrorContent from "@app/analytics/WorkspaceOverview/components/ErrorContent";

import EmptyContent from "../components/EmptyContent";
import HorizontalBarChart from "../components/HorizontalBarChart";
import { useWorkspaceOverview } from "../WorkspaceOverviewProvider";

// TODO: a ton of this code is duplicated in the MeetingSizeTabModule
interface ApiMeetingDurationData {
  observedDuration: number;
  engagement: ParsedMetric;
  meetingCount: ParsedMetric;
}

const TEN_MINUTES = 10 * 60 * 1000;

const MEETING_DURATION_BUCKETS = {
  "0": "<= 30 min",
  "1": "30-60 min",
  "2": "> 60 min",
};

// TODO: values should be inferred in the HorizontalBarChart
const BUCKET_TO_Y_VALUE = {
  "0": 25,
  "1": 50,
  "2": 75,
};

type BucketSize = keyof typeof MEETING_DURATION_BUCKETS;
type BucketAverageMap = {
  [key in BucketSize]: number;
};

const bucketByMeetingDuration = groupBy<ApiMeetingDurationData>(
  (item): BucketSize => {
    if (item.observedDuration > 60 * 60) {
      return "2";
    } else if (item.observedDuration >= 30 * 60) {
      return "1";
    }
    return "0";
  }
);

const getAverageBucketScore = (meetingData: ApiMeetingDurationData[]) => {
  const sum = reduce(
    (total, data) =>
      total + (data.engagement?.value ?? 0) * (data.meetingCount?.value ?? 0),
    0,
    meetingData
  );

  const sumWeights = reduce(
    (total, data) => total + (data.meetingCount?.value ?? 0),
    0,
    meetingData
  );

  if (sumWeights == 0) {
    return null;
  }
  return sum / sumWeights;
};

const mapMetricsToChartData = (
  metrics: ApiMeetingDurationData[] | null
): TrendSeriesItem[] => {
  if (!metrics) return [];
  const bucketed = bucketByMeetingDuration(metrics);

  // computes the weighted average Read score for each bucket
  const reducedBucketed: BucketAverageMap = reduceP(
    bucketed,
    (bucketMap, value, key) => {
      const bucketWeight = getAverageBucketScore(value);
      if (bucketWeight != null) {
        bucketMap[key as BucketSize] = Math.round(bucketWeight);
      }
      return bucketMap;
    },
    {} as BucketAverageMap
  );

  // maps the bucket average (x) to the bucket y value
  return Object.keys(BUCKET_TO_Y_VALUE).reduce((acc, key) => {
    const bucketSize = key as BucketSize;
    const yValue = BUCKET_TO_Y_VALUE[bucketSize];
    const xValue = reducedBucketed[bucketSize];
    acc.push({ x: xValue, y: yValue });
    return acc;
  }, [] as TrendSeriesItem[]);
};

const getQuery = (dateRange: DateRange): MeetOpsQuery => {
  return {
    dateRanges: [dateRange],
    dimensions: ["observedDuration"],
    metrics: ["engagement", "meetingCount"],
    filters: [Filters.multiParticipantMeeting()],
    people: "everyone",
  };
};

const DurationTabModule = () => {
  const { queryDateRanges } = useWorkspaceOverview();
  const durationQuery = useMetricsQuery(getQuery(queryDateRanges[0]), {
    staleTime: TEN_MINUTES,
  });

  const { data, isInitialLoading, isError, refetch } = durationQuery;

  const queryData: ApiMeetingDurationData[] | null =
    data?.results[0].data ?? null;

  const chartData = useMemo(
    () => mapMetricsToChartData(queryData),
    [queryData]
  );

  if (isError) {
    return <ErrorContent onRetry={refetch} sx={{ height: "240px" }} />;
  }

  if (!isInitialLoading && !data) {
    return <EmptyContent />;
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <Typography
        variant="bodySecondaryReg"
        sx={{ color: "shades.neutral.600", pb: 1 }}
      >
        Meeting duration compared to average meeting Engagement
      </Typography>
      <Box sx={{ width: "100%", height: "205px" }}>
        <ParentSize>
          {({ width, height }) => {
            if (isInitialLoading) {
              return (
                <Skeleton
                  variant="rectangular"
                  sx={{ width, height, borderRadius: "8px" }}
                />
              );
            }

            return (
              <HorizontalBarChart
                context="duration"
                width={width}
                height={height}
                data={chartData}
                label="Average Engagement"
              />
            );
          }}
        </ParentSize>
      </Box>
    </Stack>
  );
};

export default DurationTabModule;
